import React, { useEffect, useState, useMemo } from 'react';
import { formatKpiCrores } from '../utils/kpiAmountFormat';
import { adminAPI, bankGuaranteesAPI, correspondenceAPI, milestonesAPI, raBillsAPI } from '../services/authService';
import { useProjects } from '../context/ProjectsContext';
import { formatDateIN, stripBrackets } from '../utils/dateFormat';
import { safeArray, unwrapListResponse } from '../components/ErrorBoundary';
import ProjectNameText from '../components/ProjectNameText';
import KpiBreakdownTooltip from '../components/KpiBreakdownTooltip';

const Overview = ({ onNavigate, onSelectProject }) => {
  const { projects: contextProjects } = useProjects();
  const [fullProjects, setFullProjects] = useState([]);
  const [dataReady, setDataReady] = useState(false);
  const [isFallbackData, setIsFallbackData] = useState(false);
  const [correspondenceSummary, setCorrespondenceSummary] = useState(null);
  const [criticalChains, setCriticalChains] = useState([]);
  const [raBills, setRaBills] = useState([]);
  const [bankGuarantees, setBankGuarantees] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [customConfigKpis, setCustomConfigKpis] = useState([]);

  useEffect(() => {
    const loadOverviewData = async () => {
      try {
        const [summaryResp, criticalChainsResp, raResp, bgResp, milestonesResp, projectsResp] = await Promise.allSettled([
          correspondenceAPI.getSummary(),
          correspondenceAPI.getChains({ status: 'Open', priority: 'Critical', pageSize: 20, page: 1 }),
          raBillsAPI.getAll({ page: 1, pageSize: 500 }),
          bankGuaranteesAPI.getAll({ page: 1, pageSize: 30 }),
          milestonesAPI.getAll({ page: 1, pageSize: 30 }),
          adminAPI.getProjects(),
        ]);

        const projectsData = safeArray(contextProjects).map((p) => ({ id: p.id, status: p.status || 'Active' }));
        const projRes = projectsResp.status === 'fulfilled' ? projectsResp.value : null;
        const { rows: projectRows } = projRes ? unwrapListResponse(projRes) : { rows: [] };
        const mapped = safeArray(projectRows).map((row) => {
          const scope = row?.metadata?.scope || {};
          return {
            id: row.id,
            name: row.name,
            agreement_prefix: row.agreementPrefix || row.agreement_prefix || row.code || `PRJ-${row.id}`,
            status: row.status || 'Active',
            startDate: row.startDate,
            endDate: row.endDate,
            work_description: scope.work_description || '',
            agreement_value: Number(scope.agreement_value ?? 0),
            revised_value: Number(scope.revised_value ?? 0),
          };
        });
        setFullProjects(mapped);
        const summaryData = summaryResp.status === 'fulfilled' ? summaryResp.value?.data : null;
        const criticalPayload = criticalChainsResp.status === 'fulfilled' ? criticalChainsResp.value?.data : null;
        const criticalChainsData = (criticalPayload && typeof criticalPayload === 'object' && Array.isArray(criticalPayload.chains))
          ? criticalPayload.chains
          : Array.isArray(criticalPayload) ? criticalPayload : [];
        const unwrapRows = (r) => { const b = r?.data; return safeArray(Array.isArray(b) ? b : (b?.data ?? [])); };
        const raData = unwrapRows(raResp.status === 'fulfilled' ? raResp.value : null);
        const bgData = unwrapRows(bgResp.status === 'fulfilled' ? bgResp.value : null);
        const milestonesData = unwrapRows(milestonesResp.status === 'fulfilled' ? milestonesResp.value : null);

        const hasAnyApiData = (
          projectsData.length > 0
          || (summaryData && (summaryData.totalEntries > 0 || summaryData.openChains > 0))
          || criticalChainsData.length > 0
          || raData.length > 0
          || bgData.length > 0
          || milestonesData.length > 0
        );

        const apiRas = raData.map((row) => {
          const payload = row.data || {};
          const netPayable = Number(payload.net_payable ?? row.net_payable ?? row.amount ?? 0);
          const paidValue = Number(payload.paid_value ?? row.paid_value ?? 0);
          return {
            project_id: payload.project_id ?? row.project_id ?? row.projectId,
            status: row.status || payload.status || 'Submitted',
            payment_status: payload.payment_status ?? row.payment_status ?? 'Unpaid',
            net_payable: netPayable,
            paid_value: paidValue,
            upto_date_gross_work_done: Number(payload.upto_date_gross_work_done ?? row.upto_date_gross_work_done ?? row.gross_work_done ?? 0),
            balance_value: Number(payload.balance_value ?? row.balance_value ?? Math.max(0, netPayable - paidValue)),
          };
        });
        const apiBgs = bgData.map((row) => {
          const payload = row.data || {};
          return {
            project_id: payload.project_id ?? row.project_id ?? row.projectId,
            status: row.status || payload.status || 'Active',
            expiry_date: payload.expiry_date ?? row.expiry_date ?? null,
            amount: Number(row.amount ?? payload.amount ?? 0),
          };
        });
        const apiMilestones = milestonesData.map((row) => {
          const payload = row.data || {};
          return {
            project_id: payload.project_id ?? row.project_id ?? row.projectId ?? payload.projectId,
            status: row.status || payload.status || 'Submitted',
          };
        });

        if (hasAnyApiData) {
          setCorrespondenceSummary(summaryData || null);
          setCriticalChains(safeArray(criticalChainsData));
          setRaBills(safeArray(apiRas));
          setBankGuarantees(safeArray(apiBgs));
          setMilestones(safeArray(apiMilestones));
          setIsFallbackData(false);
        } else {
          setCorrespondenceSummary(null);
          setCriticalChains([]);
          setRaBills([]);
          setBankGuarantees([]);
          setMilestones([]);
          setIsFallbackData(true);
        }

      } catch (error) {
        console.warn('Overview API data unavailable:', error?.message || error);
        setCorrespondenceSummary(null);
        setCriticalChains([]);
        setRaBills([]);
        setBankGuarantees([]);
        setMilestones([]);
        setIsFallbackData(true);
      } finally {
        setDataReady(true);
      }
    };
    loadOverviewData();
  }, [contextProjects]);

  const raBillsSafe = safeArray(raBills);
  const bankGuaranteesSafe = safeArray(bankGuarantees);
  const milestonesSafe = safeArray(milestones);
  const projectsSafe = safeArray(contextProjects);

  const projectIdToUptoDateGross = useMemo(() => {
    const map = {};
    raBillsSafe.forEach((r) => {
      const pid = Number(r.project_id);
      if (!pid) return;
      const gross = r?.upto_date_gross_work_done ?? 0;
      map[pid] = Math.max(map[pid] || 0, gross);
    });
    return map;
  }, [raBillsSafe]);

  const getFinancialProgressPct = (project) => {
    const agreementVal = Number(project?.agreement_value ?? 0);
    if (!agreementVal) return 0;
    const uptoDateGross = projectIdToUptoDateGross[Number(project?.id)] || 0;
    const pct = (uptoDateGross / agreementVal) * 100;
    const capped = Math.min(100, pct);
    return Math.round(capped * 100) / 100;
  };

  const activeProjectsWithScope = fullProjects
    .filter((p) => p && p.status === 'Active' && (p.agreement_value > 0 || p.work_description))
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
  const orderBookTotal = fullProjects.reduce((s, p) => s + (p?.agreement_value ?? 0), 0);

  const totalEntries = correspondenceSummary?.totalEntries ?? 0;
  const inwardCount = correspondenceSummary?.inwardCount ?? 0;
  const outwardCount = correspondenceSummary?.outwardCount ?? 0;
  const openChainsCount = correspondenceSummary?.openChains ?? 0;
  const slaBreachedCount = correspondenceSummary?.slaBreached ?? 0;
  const criticalOpen = safeArray(criticalChains);

  const pendingCertification = raBillsSafe.filter(r => r && r.status === 'Submitted');
  const pendingCertValue = pendingCertification.reduce((s, r) => s + (r?.net_payable ?? 0), 0);

  const unpaidBills = raBillsSafe.filter(r => r && (r.payment_status === 'Unpaid' || r.payment_status === 'Part Paid'));
  const unpaidValue = unpaidBills.reduce((s, r) => s + (r?.balance_value ?? 0), 0);

  const expiringBGs = bankGuaranteesSafe.filter(bg => {
    if (!bg) return false;
    const exp = new Date(bg.expiry_date);
    const now = new Date();
    const diff = (exp - now) / (1000 * 60 * 60 * 24);
    return diff <= 30 && diff > 0 && bg.status === 'Active';
  });
  const expiringBGsAlerts = bankGuaranteesSafe.filter(bg => bg && bg.status === 'Expiring');
  const allExpiringBGs = [...expiringBGs, ...expiringBGsAlerts];
  const expiringBGAmount = allExpiringBGs.reduce((s, bg) => s + (bg?.amount ?? 0), 0);

  const pendingShiftRequests = milestonesSafe.filter(sr => sr && (sr.status === 'Under Review' || sr.status === 'Submitted'));
  const categoryBreakdown = Array.isArray(correspondenceSummary?.categoryBreakdown)
    ? correspondenceSummary.categoryBreakdown
    : [];

  const projectNameById = useMemo(() => {
    const m = {};
    safeArray(fullProjects).forEach((p) => {
      if (p?.id != null) m[Number(p.id)] = stripBrackets(p.name || '') || `Project ${p.id}`;
    });
    return m;
  }, [fullProjects]);

  const pendingPaymentBreakdown = useMemo(() => {
    const m = {};
    unpaidBills.forEach((r) => {
      const pid = Number(r.project_id);
      if (!pid) return;
      const bal = Number(r.balance_value || 0);
      if (bal <= 0) return;
      if (!m[pid]) m[pid] = { projectId: pid, projectName: projectNameById[pid] || `Project ${pid}`, bills: 0, amount: 0 };
      m[pid].amount += bal;
      m[pid].bills += 1;
    });
    return Object.values(m).sort((a, b) => b.amount - a.amount);
  }, [unpaidBills, projectNameById]);

  const pendingCertBreakdown = useMemo(() => {
    const m = {};
    pendingCertification.forEach((r) => {
      const pid = Number(r.project_id);
      if (!pid) return;
      const net = Number(r.net_payable || 0);
      if (net <= 0) return;
      if (!m[pid]) m[pid] = { projectId: pid, projectName: projectNameById[pid] || `Project ${pid}`, bills: 0, amount: 0 };
      m[pid].amount += net;
      m[pid].bills += 1;
    });
    return Object.values(m).sort((a, b) => b.amount - a.amount);
  }, [pendingCertification, projectNameById]);

  const expiringBgBreakdown = useMemo(() => {
    const m = {};
    allExpiringBGs.forEach((bg) => {
      const pid = Number(bg.project_id);
      const key = pid || 0;
      const amt = Number(bg.amount || 0);
      if (amt <= 0) return;
      const label = pid ? (projectNameById[pid] || `Project ${pid}`) : 'Unassigned / other';
      if (!m[key]) m[key] = { projectId: key, projectName: label, count: 0, amount: 0 };
      m[key].amount += amt;
      m[key].count += 1;
    });
    return Object.values(m).sort((a, b) => b.amount - a.amount);
  }, [allExpiringBGs, projectNameById]);

  const pendingShiftBreakdown = useMemo(() => {
    const m = {};
    pendingShiftRequests.forEach((sr) => {
      const pid = Number(sr.project_id);
      if (!pid) return;
      if (!m[pid]) m[pid] = { projectId: pid, projectName: projectNameById[pid] || `Project ${pid}`, count: 0 };
      m[pid].count += 1;
    });
    return Object.values(m).sort((a, b) => b.count - a.count);
  }, [pendingShiftRequests, projectNameById]);

  const formatCurrency = (val) => {
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)} Cr`;
    if (val >= 100000) return `₹${(val / 100000).toFixed(2)} L`;
    return `₹${(val / 1000).toFixed(0)}K`;
  };


  useEffect(() => {
    let cancelled = false;
    adminAPI.getRuntimeKpis({ pageKey: 'overview', compute: 1 })
      .then((res) => {
        if (cancelled) return;
        const enabled = res?.data?.enabled;
        const list = res?.data?.data?.kpis || [];
        setCustomConfigKpis(enabled ? list : []);
      })
      .catch(() => { if (!cancelled) setCustomConfigKpis([]); });
    return () => { cancelled = true; };
  }, []);

  if (!dataReady) {
    return (
      <div>
        <div className="section-header">
          <h2>Overview Dashboard</h2>
        </div>
        <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
          Loading live overview data...
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="section-header">
        <h2>Overview Dashboard</h2>
      </div>

      <div className="kpi-grid">
        <div className="kpi-card" onClick={() => onNavigate && onNavigate('project-scope')} style={{ cursor: onNavigate ? 'pointer' : 'default' }}>
          <div className="kpi-icon kpi-icon--projects" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 21h18" /><path d="M5 21V7l7-4 7 4v14" /><path d="M9 21v-6h6v6" />
            </svg>
          </div>
          <div className="kpi-card-body">
            <h4>All projects</h4>
            <p className="kpi-value">{projectsSafe.length}</p>
            <p className="kpi-sub">Active: <span className="kpi-sub-num">{projectsSafe.filter((p) => p && p.status === 'Active').length}</span> | Closed: <span className="kpi-sub-num">{projectsSafe.filter((p) => p && p.status !== 'Active').length}</span></p>
          </div>
        </div>
        <div className="kpi-card" onClick={() => onNavigate && onNavigate('project-scope')} style={{ cursor: onNavigate ? 'pointer' : 'default' }}>
          <div className="kpi-icon kpi-icon--value" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 3h12" />
              <path d="M6 8h12" />
              <path d="M6 13h3" />
              <path d="M9 13c6.667 0 6.667-10 0-10" />
              <path d="m6 13 8.5 8" />
            </svg>
          </div>
          <div className="kpi-card-body">
            <h4>Total Projects Value</h4>
            <p className="kpi-value kpi-value--amount">{formatKpiCrores(orderBookTotal)}</p>
            <p className="kpi-sub">Agreement order book</p>
          </div>
        </div>
        <div className="kpi-card" onClick={() => onNavigate && onNavigate('correspondence-all')} style={{ cursor: onNavigate ? 'pointer' : 'default' }}>
          <div className="kpi-icon kpi-icon--mail" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 7L2 7" />
            </svg>
          </div>
          <div className="kpi-card-body">
            <h4>Correspondence</h4>
            <p className="kpi-value">{totalEntries}</p>
            <p className="kpi-sub">Inward: <span className="kpi-sub-num">{inwardCount}</span> | Outward: <span className="kpi-sub-num">{outwardCount}</span></p>
          </div>
        </div>
        <div className="kpi-card" onClick={() => onNavigate && onNavigate('correspondence-open')} style={{ cursor: onNavigate ? 'pointer' : 'default' }}>
          <div className="kpi-icon kpi-icon--dues" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
            </svg>
          </div>
          <div className="kpi-card-body">
            <h4>Reply dues</h4>
            <p className="kpi-value" style={{ color: '#ef4444' }}>{slaBreachedCount}</p>
            <p className="kpi-sub">Open entries past due date</p>
          </div>
        </div>
      </div>
      {isFallbackData && (
        <p style={{ marginTop: 8, fontSize: 12, color: '#b45309' }}>
          {projectsSafe.length === 0 && !correspondenceSummary ? 'Unable to load data from API.' : 'Some data may be unavailable.'}
        </p>
      )}


      {customConfigKpis.length > 0 && (
        <div id="overview-config-kpis" style={{ marginTop: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 16 }}>Configured KPIs</h3>
          <div className="kpi-grid">
            {customConfigKpis.map((k) => (
              <div
                key={k.kpiKey}
                className="kpi-card"
                onClick={() => k.drillDownTabKey && onNavigate && onNavigate(k.drillDownTabKey)}
                style={{ cursor: k.drillDownTabKey && onNavigate ? 'pointer' : 'default' }}
              >
                <div className="kpi-card-body">
                  <h4>{k.name}</h4>
                  <p className="kpi-value">{k.value == null ? '—' : k.value}</p>
                  <p className="kpi-sub">{k.aggregation} · {k.dataSourceKey}{k.error ? ` · ${k.error}` : ''}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div id="overview-financial-kpis" style={{ marginTop: 24 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 16 }}>Financial KPIs</h3>
        <div className="stats-grid">
          <KpiBreakdownTooltip
            title="RA pending certification"
            subtitle="Submitted RA bills with net payable by project (hover for breakdown)."
            columns={[
              { key: 'projectName', label: 'Project' },
              { key: 'bills', label: 'Bills', align: 'right', format: 'int' },
              { key: 'amount', label: 'Net payable (₹)', align: 'right', format: 'inr' },
            ]}
            rows={pendingCertBreakdown}
            emptyMessage="No submitted RA bills with positive net payable."
          >
            <div className="stat-card">
              <div className="stat-icon grad-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2h12v20l-2-1.5L14 22l-2-1.5L10 22l-2-1.5L6 22V2z" />
                  <line x1="9" y1="8" x2="15" y2="8" />
                  <line x1="9" y1="12" x2="15" y2="12" />
                  <line x1="9" y1="16" x2="13" y2="16" />
                </svg>
              </div>
              <div className="stat-info">
                <p className="stat-label">RA Pending Certification</p>
                <p className="stat-value">{pendingCertification.length}</p>
                <p className="stat-sub">Value: {formatKpiCrores(pendingCertValue)}</p>
              </div>
            </div>
          </KpiBreakdownTooltip>
          <KpiBreakdownTooltip
            title="RA pending payment"
            subtitle="Unpaid / part-paid RA bills with outstanding balance, grouped by project."
            columns={[
              { key: 'projectName', label: 'Project' },
              { key: 'bills', label: 'Bills', align: 'right', format: 'int' },
              { key: 'amount', label: 'Balance (₹)', align: 'right', format: 'inr' },
            ]}
            rows={pendingPaymentBreakdown}
            emptyMessage="No outstanding RA payment balance by project."
          >
            <div className="stat-card">
              <div className="stat-icon stat-icon--danger" style={{ background: '#fee2e2' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <div className="stat-info">
                <p className="stat-label">RA Pending Payment</p>
                <p className="stat-value">{unpaidBills.length}</p>
                <p className="stat-sub">Balance: {formatKpiCrores(unpaidValue)}</p>
              </div>
            </div>
          </KpiBreakdownTooltip>
          <KpiBreakdownTooltip
            title="BG expiring within 30 days"
            subtitle="Active / expiring bank guarantees due soon, by project."
            columns={[
              { key: 'projectName', label: 'Project' },
              { key: 'count', label: 'BGs', align: 'right', format: 'int' },
              { key: 'amount', label: 'Amount (₹)', align: 'right', format: 'inr' },
            ]}
            rows={expiringBgBreakdown}
            emptyMessage="No expiring BG amount in the selected window."
          >
            <div className="stat-card">
              <div className="stat-icon stat-icon--warning" style={{ background: '#fef3c7' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" />
                </svg>
              </div>
              <div className="stat-info">
                <p className="stat-label">BG Expiring (30 days)</p>
                <p className="stat-value">{allExpiringBGs.length}</p>
                <p className="stat-sub">Amount: {formatKpiCrores(expiringBGAmount)}</p>
              </div>
            </div>
          </KpiBreakdownTooltip>
          <KpiBreakdownTooltip
            title="EOT / milestone shift — pending approval"
            subtitle="Submitted or under-review shift requests by project."
            columns={[
              { key: 'projectName', label: 'Project' },
              { key: 'count', label: 'Requests', align: 'right', format: 'int' },
            ]}
            rows={pendingShiftBreakdown}
            emptyMessage="No project-linked pending requests (check API includes project on milestones)."
          >
            <div className="stat-card">
              <div className="stat-icon grad-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
              </div>
              <div className="stat-info">
                <p className="stat-label">EOT / Milestone Shift Pending</p>
                <p className="stat-value">{pendingShiftRequests.length}</p>
                <p className="stat-sub">Awaiting approval</p>
              </div>
            </div>
          </KpiBreakdownTooltip>
        </div>
      </div>

      {activeProjectsWithScope.length > 0 && (
        <div id="overview-active-projects" style={{ marginTop: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 16 }}>Active Projects</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 20 }}>
            {activeProjectsWithScope.map((project) => {
              const finPct = getFinancialProgressPct(project);
              return (
              <div
                key={project.id}
                role="button"
                tabIndex={0}
                onClick={() => onSelectProject && onSelectProject(project.id)}
                onKeyDown={(e) => e.key === 'Enter' && onSelectProject && onSelectProject(project.id)}
                className="overview-project-card"
                style={{
                  overflow: 'hidden',
                  cursor: onSelectProject ? 'pointer' : 'default',
                }}
              >
                <div className="grad-accent-bar" />
                <div style={{ padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div className="overview-project-card__icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
                          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                        </svg>
                      </div>
                      <div>
                        <h4 style={{ fontSize: 15, fontWeight: 600, color: '#1f2937', margin: 0 }}>
                          <ProjectNameText as="span">{stripBrackets(project.name)}</ProjectNameText>
                        </h4>
                        <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0 0' }}>{project.agreement_prefix}</p>
                      </div>
                    </div>
                    <span style={{ background: '#dcfce7', color: '#166534', fontSize: 11, fontWeight: 600, padding: '4px 8px', borderRadius: 6 }}>Active</span>
                  </div>
                  <p style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5, marginBottom: 12, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                    {stripBrackets(project.work_description || 'No description')}
                  </p>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(108px, 1fr))',
                      gap: 14,
                      marginBottom: 12,
                    }}
                  >
                    <div>
                      <p style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 2px 0' }}>Agreement value</p>
                      <p style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', margin: 0 }}>{formatCurrency(project.agreement_value)}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 2px 0' }}>Date of start</p>
                      <p style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', margin: 0 }}>{project.startDate ? formatDateIN(project.startDate) : '—'}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 2px 0' }}>Stip. date of completion</p>
                      <p style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', margin: 0 }}>{project.endDate ? formatDateIN(project.endDate) : '—'}</p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ flex: 1, marginRight: 8 }}>
                      <p style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 4px 0' }}>Financial progress</p>
                      <div className="overview-project-card__progress-track">
                        <div className="overview-project-card__progress-fill" style={{ width: `${Math.min(100, finPct)}%` }} />
                      </div>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginLeft: 8, fontVariantNumeric: 'tabular-nums' }}>{finPct.toFixed(2)}%</span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" style={{ width: 18, height: 18, flexShrink: 0, marginLeft: 4 }}><polyline points="9 18 15 12 9 6" /></svg>
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        </div>
      )}

      <div id="overview-urgent-actions" style={{ marginTop: 24 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 16 }}>Urgent Actions - Critical Open Items</h3>
        {criticalOpen.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <h3>No Critical Items</h3>
            <p>All critical correspondence items have been resolved.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Type</th>
                  <th>Entries</th>
                  <th>Last letter date</th>
                  <th>Targeted Reply</th>
                </tr>
              </thead>
              <tbody>
                {criticalOpen.map((chain) => (
                  <tr key={chain.id}>
                    <td style={{ fontWeight: 500 }}>{chain.subject}</td>
                    <td><span className="status-badge critical">{chain.type}</span></td>
                    <td>{chain.entry_count}</td>
                    <td>{formatDateIN(chain.last_entry_date)}</td>
                    <td>
                      {chain.sla_breached ? (
                        <span className="status-badge sla-breached">Breached</span>
                      ) : (
                        <span className="status-badge on-track">On Track</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div id="overview-category-breakdown" style={{ marginTop: 24 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 16 }}>Correspondence by Category</h3>
        {categoryBreakdown.length === 0 ? (
          <div className="empty-state">
            <h3>No Category Data</h3>
            <p>Category-wise correspondence will appear here.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Total Threads</th>
                  <th>Open Threads</th>
                  <th>Closed Threads</th>
                </tr>
              </thead>
              <tbody>
                {categoryBreakdown.map((item) => (
                  <tr key={item.category}>
                    <td><span className="category-tag">{item.category}</span></td>
                    <td>{item.total}</td>
                    <td><span className="status-badge open">{item.open}</span></td>
                    <td><span className="status-badge closed">{item.total - item.open}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
};

export default Overview;

